import Chat from './Chat';
import TrackingMap from './TrackingMap';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import io from 'socket.io-client';

// ==================== EMERGENCY LOCATOR COMPONENT ====================
const EmergencyLocator = ({ driverId, onEmergencyTriggered }) => {
  const [isEmergencyMode, setIsEmergencyMode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [ringing, setRinging] = useState(false);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const socketRef = useRef(null);
  const locationIntervalRef = useRef(null);

  useEffect(() => {
    registerPushNotifications();

    socketRef.current = io('/');
    socketRef.current.emit('register_driver', driverId);

    socketRef.current.on('emergency_trigger', (data) => {
      if (data.driverId === driverId) {
        triggerEmergencyAlert(data);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      clearInterval(intervalRef.current);
      if (locationIntervalRef.current) {
        clearInterval(locationIntervalRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    };
  }, [driverId]);

  const registerPushNotifications = async () => {
    try {
      if (!('Notification' in window)) {
        console.log('This browser does not support notifications');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return;
      }

      if (!('serviceWorker' in navigator)) {
        console.log('Service workers not supported');
        return;
      }

      const registration = await navigator.serviceWorker.ready;

      if (!registration.pushManager) {
        console.log('Push manager not available');
        return;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
      });

      const phoneNumber = localStorage.getItem('driverPhone') || '';

      await fetch('/api/jobs/emergency/register-push', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driverId,
          pushSubscription: subscription,
          phoneNumber: phoneNumber
        })
      });

      console.log('✅ Push notification registered successfully');
    } catch (error) {
      console.error('Push registration error:', error);
    }
  };

  const triggerEmergencyAlert = async (data) => {
    setIsEmergencyMode(true);
    setCountdown(5);
    setRinging(true);

    if (audioRef.current) {
      audioRef.current.loop = true;
      audioRef.current.volume = 1.0;
      try {
        await audioRef.current.play();
      } catch (e) {
        console.error('Audio play error:', e);
      }
    }

    sendEmergencyLocation();

    intervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          sendEmergencyLocation();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    locationIntervalRef.current = setInterval(sendEmergencyLocation, 5000);

    onEmergencyTriggered && onEmergencyTriggered(data);
  };

  const sendEmergencyLocation = async () => {
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });

      await fetch('/api/jobs/emergency/location-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driverId,
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        })
      });

      console.log('📍 Emergency location sent:', position.coords.latitude, position.coords.longitude);
    } catch (error) {
      console.error('Location send error:', error);
    }
  };

  const acknowledgeSafe = async () => {
    clearInterval(intervalRef.current);
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
    }
    setRinging(false);
    setIsEmergencyMode(false);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    await fetch('/api/jobs/emergency/acknowledge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ driverId })
    });
  };

  const callEmergencyServices = () => {
    window.location.href = 'tel:10111';
  };

  return (
    <>
      <audio
        ref={audioRef}
        src="/emergency-alarm.mp3"
        preload="auto"
        style={{ display: 'none' }}
      />

      {isEmergencyMode && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(255, 0, 0, 0.95)',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            animation: 'pulse 0.5s ease-in-out infinite alternate',
            fontSize: '80px',
            marginBottom: '20px'
          }}>
            🚨
          </div>

          <h1 style={{ color: 'white', fontSize: '32px', textAlign: 'center', margin: '0 0 10px 0' }}>
            EMERGENCY LOCATE ACTIVE
          </h1>

          <p style={{ color: '#ffcccc', fontSize: '20px', textAlign: 'center', margin: '0 0 20px 0' }}>
            Your location is being shared with admin.
            {ringing && ' 📍 GPS tracking active'}
          </p>

          {countdown > 0 && (
            <p style={{ color: 'white', fontSize: '64px', fontWeight: 'bold', margin: '0 0 20px 0' }}>
              {countdown}
            </p>
          )}

          <div style={{
            padding: '20px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '12px',
            textAlign: 'center',
            marginBottom: '20px',
            width: '100%',
            maxWidth: '400px'
          }}>
            <p style={{ color: 'white', fontSize: '14px', margin: '0 0 5px 0' }}>
              📍 Location sharing active
            </p>
            <p style={{ color: '#ffcccc', fontSize: '12px', margin: '0' }}>
              {ringing ? '🟢 High accuracy mode' : '📍 Standard tracking'}
            </p>
          </div>

          <button
            onClick={acknowledgeSafe}
            style={{
              padding: '16px 40px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              width: '100%',
              maxWidth: '400px',
              marginBottom: '12px'
            }}
          >
            ✅ I AM SAFE - STOP EMERGENCY
          </button>

          <button
            onClick={callEmergencyServices}
            style={{
              padding: '16px 40px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: '2px solid white',
              borderRadius: '12px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              width: '100%',
              maxWidth: '400px'
            }}
          >
            🆘 CALL EMERGENCY SERVICES
          </button>

          <style>
            {`
              @keyframes pulse {
                from { transform: scale(1); opacity: 1; }
                to { transform: scale(1.2); opacity: 0.7; }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

// ==================== END EMERGENCY LOCATOR COMPONENT ====================

const API_BASE = '/api';

const useDriverLocation = (driverId, activeJobRef) => {
  const [locationStatus, setLocationStatus] = useState('idle');
  const [currentLocation, setCurrentLocation] = useState(null);
  const watchIdRef = useRef(null);

  const startWatching = useCallback(() => {
    if (watchIdRef.current != null) return;

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
        setCurrentLocation(loc);

        const job = activeJobRef.current;
        if (job?.id) {
          const token = localStorage.getItem('token');
          fetch(`${API_BASE}/jobs/driver/${driverId}/location`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ lat: loc.lat, lng: loc.lng, jobId: job.id }),
          }).catch((err) => console.error('Location push error:', err));
        }
      },
      (err) => console.error('watchPosition error:', err),
      { enableHighAccuracy: true }
    );
  }, [driverId, activeJobRef]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) { setLocationStatus('unsupported'); return; }

    const doGetPosition = () => {
      setLocationStatus('requesting');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationStatus('granted');
          const loc = { lat: position.coords.latitude, lng: position.coords.longitude };
          setCurrentLocation(loc);
          startWatching();
        },
        (err) => setLocationStatus(err.code === 1 ? 'denied' : 'error'),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    };

    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' })
        .then((result) => result.state === 'denied' ? setLocationStatus('denied') : doGetPosition())
        .catch(() => doGetPosition());
    } else {
      doGetPosition();
    }
  }, [startWatching]);

  useEffect(() => {
    if (!navigator.geolocation) { setLocationStatus('unsupported'); return; }
    if (!navigator.permissions) return;
    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      if (result.state === 'granted') { setLocationStatus('granted'); startWatching(); }
      else if (result.state === 'denied') { setLocationStatus('denied'); }
    }).catch(() => {});
  }, [startWatching]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  return { locationStatus, currentLocation, requestLocation };
};

const LocationPermissionBanner = ({ status, onRequest }) => {
  if (status === 'granted') return null;

  if (status === 'idle') return (
    <div style={{ borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', backgroundColor: '#1a2540', border: '1px solid #3b82f6' }}>
      <div>
        <div style={{ color: '#60a5fa', fontWeight: 'bold', marginBottom: '4px' }}>📍 Share Your Location</div>
        <div style={{ color: '#e5e7eb', fontSize: '13px' }}>Allow location access so customers can see your live position on the map.</div>
      </div>
      <button onClick={onRequest} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap' }}>
        📍 Enable My Location
      </button>
    </div>
  );

  if (status === 'requesting') return (
    <div style={{ borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', backgroundColor: '#1a2540', border: '1px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <span style={{ fontSize: '20px' }}>⏳</span>
      <div style={{ color: '#93c5fd', fontWeight: 'bold' }}>Waiting for permission… please tap Allow in the browser prompt.</div>
    </div>
  );

  if (status === 'denied') return (
    <div style={{ borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', backgroundColor: '#3b0000', border: '1px solid #ef4444' }}>
      <div style={{ color: '#ef4444', fontWeight: 'bold', marginBottom: '6px' }}>🚫 Location Access Blocked</div>
      <div style={{ color: '#e5e7eb', fontSize: '13px', lineHeight: '1.5' }}>
        Click the 🔒 lock icon in your browser address bar → <strong>Site settings</strong> → <strong>Location</strong> → <strong>Allow</strong>, then refresh.
      </div>
    </div>
  );

  return null;
};

const TurnByTurnPanel = ({ routeInfo, destinationType }) => {
  const [expanded, setExpanded] = useState(false);

  if (!routeInfo) {
    return (
      <div style={{ backgroundColor: '#1f2937', borderRadius: '10px', padding: '16px', marginTop: '16px', textAlign: 'center' }}>
        <div style={{ color: '#9ca3af', fontSize: '13px' }}>⏳ Calculating route...</div>
      </div>
    );
  }

  const steps = routeInfo.steps || [];
  const icon = destinationType === 'pickup' ? '📍' : '📦';

  return (
    <div style={{ backgroundColor: '#1f2937', borderRadius: '10px', padding: '16px', marginTop: '16px', borderLeft: `4px solid ${destinationType === 'pickup' ? '#3b82f6' : '#eab308'}` }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <div style={{ fontSize: '12px', color: '#9ca3af', textTransform: 'uppercase' }}>
            {icon} {destinationType === 'pickup' ? 'TO PICKUP' : 'TO DROP-OFF'}
          </div>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: destinationType === 'pickup' ? '#3b82f6' : '#eab308' }}>
            {routeInfo.dist} km • ~{routeInfo.time} min
          </div>
        </div>
        <div style={{ fontSize: '20px', color: '#6b7280' }}>{expanded ? '▲' : '▼'}</div>
      </div>

      {expanded && steps.length > 0 && (
        <div style={{ marginTop: '16px', maxHeight: '300px', overflowY: 'auto', borderTop: '1px solid #374151', paddingTop: '12px' }}>
          {steps.map((step, idx) => (
            <div key={idx} style={{ padding: '8px 0', borderBottom: '1px solid #2d3748', fontSize: '13px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <div style={{ minWidth: '40px', color: '#eab308', fontWeight: 'bold' }}>{step.distance}km</div>
              <div style={{ color: '#d1d5db', flex: 1 }} dangerouslySetInnerHTML={{ __html: step.instruction }} />
            </div>
          ))}
        </div>
      )}

      {expanded && steps.length === 0 && (
        <div style={{ marginTop: '12px', color: '#9ca3af', textAlign: 'center', fontSize: '13px' }}>
          No turn-by-turn instructions available
        </div>
      )}
    </div>
  );
};

// ==================== VEHICLE GIF MAPPING ====================
const vehicleGifs = {
  bike: {
    gif: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExb3FhNGJxdm14cWx0a2I3a2d1cW9jMWp0a2FvbHZ6cWJzZ3U2b2VyYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7TKz8b4wK8x5Q7tG/giphy.gif',
    label: '🏍️ Bike',
    color: '#60a5fa'
  },
  'private car': {
    gif: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExYzV4a3JvY2VzZ3U4bGp5dGZ5cGp6bG9nZ2VzZ3U4bGp5dGZ5cGp6bG9nZ2VzZ3U4bGp5dGZ5cGp6bG9nZyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7TKBz3M8D5r2w6gK/giphy.gif',
    label: '🚗 Private Car',
    color: '#c084fc'
  },
  car: {
    gif: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExYzV4a3JvY2VzZ3U4bGp5dGZ5cGp6bG9nZ2VzZ3U4bGp5dGZ5cGp6bG9nZ2VzZ3U4bGp5dGZ5cGp6bG9nZyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7TKBz3M8D5r2w6gK/giphy.gif',
    label: '🚗 Private Car',
    color: '#c084fc'
  },
  'passenger+cargo': {
    gif: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExb3FhNGJxdm14cWx0a2I3a2d1cW9jMWp0a2FvbHZ6cWJzZ3U2b2VyYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7TKz8b4wK8x5Q7tG/giphy.gif',
    label: '🚐 Passenger + Cargo',
    color: '#67e8f9'
  },
  van: {
    gif: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExb3FhNGJxdm14cWx0a2I3a2d1cW9jMWp0a2FvbHZ6cWJzZ3U2b2VyYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7TKz8b4wK8x5Q7tG/giphy.gif',
    label: '🚐 Van',
    color: '#34d399'
  },
  'van+trailer': {
    gif: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExb3FhNGJxdm14cWx0a2I3a2d1cW9jMWp0a2FvbHZ6cWJzZ3U2b2VyYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7TKz8b4wK8x5Q7tG/giphy.gif',
    label: '🚛 Van + Trailer',
    color: '#ef4444'
  },
  default: {
    gif: 'https://media.giphy.com/media/v1.Y2lkPTc5MGI3NjExb3FhNGJxdm14cWx0a2I3a2d1cW9jMWp0a2FvbHZ6cWJzZ3U2b2VyYyZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/3o7TKz8b4wK8x5Q7tG/giphy.gif',
    label: '🚗 Vehicle',
    color: '#9ca3af'
  }
};

const getVehicleGif = (vehicleType) => {
  if (!vehicleType) return vehicleGifs.default;
  const type = vehicleType.toLowerCase().trim();

  if (vehicleGifs[type]) return vehicleGifs[type];

  if (type.includes('bike')) return vehicleGifs.bike;
  if (type.includes('car')) return vehicleGifs.car;
  if (type.includes('passenger') || type.includes('cargo')) return vehicleGifs['passenger+cargo'];
  if (type.includes('van') && type.includes('trailer')) return vehicleGifs['van+trailer'];
  if (type.includes('van')) return vehicleGifs.van;

  return vehicleGifs.default;
};

const renderInventory = (inventory) => {
  if (!inventory) return null;

  const items = Object.entries(inventory).filter(([_, qty]) => qty > 0);
  if (items.length === 0) return null;

  const itemEmojis = {
    'food': '🍔',
    'groceries': '🛒',
    'electronics': '📱',
    'clothing': '👕',
    'documents': '📄',
    'medicine': '💊',
    'furniture': '🪑',
    'packages': '📦',
    'parcels': '📮',
    'other': '📦'
  };

  return (
    <div style={{
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: '#1a1a2e',
      borderRadius: '6px',
      borderLeft: '3px solid #eab308'
    }}>
      <div style={{
        fontSize: '12px',
        color: '#eab308',
        fontWeight: 'bold',
        marginBottom: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
      }}>
        <span>📦</span> Items to Deliver
        <span style={{
          fontSize: '10px',
          color: '#9ca3af',
          fontWeight: 'normal',
          marginLeft: 'auto'
        }}>
          {items.length} item{items.length > 1 ? 's' : ''}
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {items.map(([item, qty]) => {
          const emoji = itemEmojis[item.toLowerCase()] || '📦';
          const displayName = item.replace(/_/g, ' ').toUpperCase();
          return (
            <span key={item} style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: '#2d2d44',
              padding: '3px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              color: '#d1d5db',
              border: '1px solid #374151'
            }}>
              <span>{emoji}</span>
              <span>{displayName}</span>
              <span style={{
                backgroundColor: '#eab308',
                color: 'black',
                borderRadius: '50%',
                padding: '0px 6px',
                fontSize: '10px',
                fontWeight: 'bold',
                minWidth: '18px',
                textAlign: 'center'
              }}>
                {qty}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
};

const calculateDistance = (lat1, lng1, lat2, lng2) => {
  if (!lat1 || !lng1 || !lat2 || !lng2) return null;

  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c;

  return distance;
};

const calculateEstimatedTime = (distance, vehicleType) => {
  if (!distance) return null;

  const speeds = {
    'bike': 25,
    'car': 35,
    'private car': 35,
    'van': 30,
    'passenger+cargo': 30,
    'van+trailer': 25,
    'default': 30
  };

  const speed = speeds[vehicleType?.toLowerCase()] || speeds.default;
  const timeHours = distance / speed;
  const timeMinutes = Math.round(timeHours * 60);

  return timeMinutes;
};

const formatDistance = (distance) => {
  if (!distance) return 'N/A';
  if (distance < 1) {
    return `${Math.round(distance * 1000)}m`;
  }
  return `${distance.toFixed(1)}km`;
};

const DriverDashboard = ({ driverId, driverName }) => {
  const [availableJobs, setAvailableJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [earnings, setEarnings] = useState(0);
  const [jobsCompleted, setJobsCompleted] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pinInput, setPinInput] = useState('');
  const [showPinModal, setShowPinModal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [routeInfo, setRouteInfo] = useState(null);
  const mapContainerRef = useRef(null);

  const [followDriver, setFollowDriver] = useState(false);
  const driverMapRef = useRef(null);

  const [showProfileModal, setShowProfileModal] = useState(false);
  const [driverProfile, setDriverProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [showBankingModal, setShowBankingModal] = useState(false);
  const [bankingDetails, setBankingDetails] = useState({
    bank_name: '',
    branch_code: '',
    account_number: '',
    account_name: ''
  });
  const [savedBanking, setSavedBanking] = useState(null);
  const [loadingBanking, setLoadingBanking] = useState(false);

  const [walletBalance, setWalletBalance] = useState(0);

  const [emergencyTriggered, setEmergencyTriggered] = useState(false);

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubReason, setCancelSubReason] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const socketRef = useRef(null);
  const activeJobRef = useRef(activeJob);
  useEffect(() => { activeJobRef.current = activeJob; }, [activeJob]);

  const { locationStatus, currentLocation, requestLocation } = useDriverLocation(driverId, activeJobRef);

  useEffect(() => {
    if (currentLocation && driverId) {
      const updateDriverLocation = async () => {
        try {
          const token = localStorage.getItem('token');
          await fetch(`${API_BASE}/jobs/update-driver-location`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ driver_id: driverId, lat: currentLocation.lat, lng: currentLocation.lng }),
          });
        } catch (err) { console.error('Driver location update error:', err); }
      };
      updateDriverLocation();
    }
  }, [currentLocation, driverId]);

  const fetchDriverProfile = async () => {
    setProfileLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/jobs/driver/${driverId}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setDriverProfile(data.driver);
        if (data.driver.wallet_balance_zar !== undefined) {
          setWalletBalance(data.driver.wallet_balance_zar);
        }
        if (data.driver.phone) {
          localStorage.setItem('driverPhone', data.driver.phone);
        }
      } else {
        console.error('Profile fetch error:', data.message);
      }
    } catch (err) {
      console.error('Fetch profile error:', err);
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchBankingDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/jobs/driver/${driverId}/banking`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.banking) {
        setSavedBanking(data.banking);
      }
    } catch (err) {
      console.error('Fetch banking error:', err);
    }
  };

  const saveBankingDetails = async () => {
    if (!bankingDetails.bank_name || !bankingDetails.branch_code || !bankingDetails.account_number || !bankingDetails.account_name) {
      alert('Please fill all fields (Bank Name, Branch Code, Account Number, Account Name)');
      return;
    }
    setLoadingBanking(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/jobs/driver/${driverId}/banking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bankingDetails)
      });
      const data = await res.json();
      if (data.success) {
        alert('Banking details saved successfully');
        setSavedBanking(data.banking);
        setShowBankingModal(false);
        fetchDriverProfile();
      } else {
        alert('Failed: ' + data.message);
      }
    } catch (err) {
      console.error(err);
      alert('Network error');
    } finally {
      setLoadingBanking(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found – driver not logged in');
        setAvailableJobs([]);
        setLoading(false);
        return;
      }

      let nearbyJobs = [];
      if (currentLocation && currentLocation.lat && currentLocation.lng) {
        const radius = 20;
        const jobsRes = await fetch(`${API_BASE}/jobs/nearby-jobs?driver_lat=${currentLocation.lat}&driver_lng=${currentLocation.lng}&radius_km=${radius}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const jobsData = await jobsRes.json();
        nearbyJobs = Array.isArray(jobsData) ? jobsData : [];
      } else {
        const jobsRes = await fetch(`${API_BASE}/jobs/paid-available`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const jobsData = await jobsRes.json();
        nearbyJobs = Array.isArray(jobsData) ? jobsData : [];
      }

      const [earningsRes, activeRes] = await Promise.all([
        fetch(`${API_BASE}/jobs/driver/${driverId}/earnings`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_BASE}/jobs/driver/${driverId}/active`, { headers: { 'Authorization': `Bearer ${token}` } }),
      ]);
      const earningsData = await earningsRes.json();
      const activeData = await activeRes.json();

      const jobsWithDistance = nearbyJobs.map(job => {
        let distance = null;
        let estimatedTime = null;

        if (currentLocation && currentLocation.lat && currentLocation.lng && job.pickup_lat && job.pickup_lng) {
          distance = calculateDistance(
            currentLocation.lat,
            currentLocation.lng,
            job.pickup_lat,
            job.pickup_lng
          );
          estimatedTime = calculateEstimatedTime(distance, job.vehicle_type);
        }

        return {
          ...job,
          distance,
          estimatedTime
        };
      });

      jobsWithDistance.sort((a, b) => {
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      });

      setAvailableJobs(jobsWithDistance);
      if (earningsData.success) {
        setEarnings(earningsData.summary.total_earned_zar);
        setJobsCompleted(earningsData.summary.jobs_completed);
      }
      setActiveJob(activeData.success && activeData.job ? activeData.job : null);
      setLoading(false);
    } catch (err) {
      console.error('Fetch error:', err);
      setAvailableJobs([]);
      setLoading(false);
    }
  }, [driverId, currentLocation]);

  const cancelTrip = async () => {
    if (!cancelReason) {
      alert('Please select a reason for cancellation');
      return;
    }

    setIsCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/jobs/cancel/${activeJob.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          driver_id: driverId,
          reason: cancelReason,
          sub_reason: cancelSubReason,
          cancelled_by: 'driver'
        })
      });

      const data = await response.json();
      if (data.success) {
        alert(`✅ Trip cancelled successfully!\n\nReason: ${cancelReason}${cancelSubReason ? `\nDetails: ${cancelSubReason}` : ''}`);
        setShowCancelModal(false);
        setShowConfirmDialog(false);
        setCancelReason('');
        setCancelSubReason('');
        setRouteInfo(null);
        fetchData();
      } else {
        alert('❌ Failed to cancel trip: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Cancel trip error:', err);
      alert('❌ Network error while cancelling trip. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelClick = () => {
    setShowConfirmDialog(true);
  };

  const confirmCancel = () => {
    setShowConfirmDialog(false);
    setShowCancelModal(true);
  };

  const cancelReasons = [
    { value: 'customer_unavailable', label: '👤 Customer Unavailable' },
    { value: 'customer_cancelled', label: '📱 Customer Cancelled' },
    { value: 'wrong_address', label: '📍 Wrong Address Provided' },
    { value: 'vehicle_issue', label: '🚗 Vehicle Issue / Breakdown' },
    { value: 'traffic_weather', label: '🌧️ Traffic / Weather Conditions' },
    { value: 'driver_emergency', label: '🆘 Driver Emergency' },
    { value: 'duplicate_job', label: '🔄 Duplicate Job' },
    { value: 'other', label: '📝 Other' }
  ];

  const audioRef = useRef(null);
  const isAudioUnlocked = useRef(false);

  useEffect(() => {
    const socket = io('/');
    socketRef.current = socket;

    socket.on('connect', () => console.log('✅ Socket connected'));
    socket.on('connect_error', (err) => console.error('❌ Socket connection error:', err));

    socket.on('new_job_available', (data) => {
      console.log('🔔 New job available event received:', data);
      fetchData();

      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.warn('Audio play error:', e));
      }

      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification('New Delivery Available!', {
            body: `Pickup: ${data.pickup?.substring(0, 50)} → ${data.dropoff?.substring(0, 50)}`
          });
        } catch (e) {
          console.warn('Notification error:', e);
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission !== 'granted' && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const audio = new Audio('/notification.mp3');
    audio.preload = 'auto';
    audioRef.current = audio;

    const unlockAudio = () => {
      if (isAudioUnlocked.current) return;
      audio.play()
        .then(() => {
          isAudioUnlocked.current = true;
          console.log('🔊 Audio unlocked');
          audio.pause();
          audio.currentTime = 0;
        })
        .catch(e => console.warn('Unlock failed:', e));
    };

    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => document.addEventListener(event, unlockAudio));
    return () => {
      events.forEach(event => document.removeEventListener(event, unlockAudio));
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    fetchData();
    fetchBankingDetails();
    fetchDriverProfile();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await mapContainerRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch { setIsFullscreen(f => !f); }
  };

  // ==================== 🗺️ GOOGLE MAPS NAVIGATION ====================
  const openInGoogleMaps = (lat, lng) => {
    if (!lat || !lng) {
      alert('Destination location not available for this job.');
      return;
    }
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
    window.open(url, '_blank');
  };

  const acceptJob = async (jobId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/jobs/accept/${jobId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ driver_id: driverId }),
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ Job accepted! Follow the route to: ${data.job.pickup_address}`);
        fetchData();
      } else {
        alert(`❌ Failed to accept job: ${data.message || 'Unknown error'}`);
        console.error('Accept job error response:', data);
      }
    } catch (err) {
      console.error('Accept job network error:', err);
      alert('Network error while accepting job. Please check your connection and try again.');
    }
  };

  const updateStatus = async (jobId, status, pin = null) => {
    try {
      const token = localStorage.getItem('token');
      const body = { status, driver_id: driverId };
      if (pin) body.pin_provided = pin;
      const res = await fetch(`${API_BASE}/jobs/status/${jobId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        if (status === 'picked_up') {
          alert('✅ Pickup confirmed! Now following route to drop-off location.');
        } else {
          alert(`✅ Job marked as ${status}!`);
        }
        setShowPinModal(false);
        setPinInput('');
        setRouteInfo(null);
        fetchData();
      } else {
        alert('Failed to update status: ' + data.message);
      }
    } catch { alert('Error updating status'); }
  };

  const driverDisplayLat = currentLocation?.lat ?? activeJob?.current_lat ?? activeJob?.pickup_lat;
  const driverDisplayLng = currentLocation?.lng ?? activeJob?.current_lng ?? activeJob?.pickup_lng;

  const [showDropdown, setShowDropdown] = useState(false);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDropdown && !event.target.closest('.dropdown-container')) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDropdown]);

  const contactAdmin = () => {
    const subject = encodeURIComponent('Driver Support Request');
    const body = encodeURIComponent(
      `Hello Admin,\n\n` +
      `I am a driver on the VTA platform and need assistance with the following:\n\n` +
      `Name: ${driverName}\n` +
      `Driver ID: ${driverId}\n\n` +
      `Issue Description:\n` +
      `[Please describe your issue here]\n\n` +
      `Thank you for your assistance.`
    );

    window.location.href = `mailto:elitenel@yahoo.com?cc=projectorion@yahoo.com&subject=${subject}&body=${body}`;
  };

  const s = {
    page: { minHeight: '100vh', backgroundColor: '#111827', color: 'white', padding: '20px', fontFamily: 'sans-serif' },
    card: { backgroundColor: '#1f2937', borderRadius: '12px', padding: '20px', marginBottom: '20px' },
    activeCard: { backgroundColor: '#1f2937', borderRadius: '12px', padding: '20px', marginBottom: '20px', border: '2px solid #eab308' },
    label: { color: '#9ca3af', fontSize: '12px', marginBottom: '2px' },
    value: { fontWeight: 'bold' },
    input: { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', boxSizing: 'border-box' },
    yellowBtn: { backgroundColor: '#eab308', color: 'black', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
    greenBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
    redBtn: { backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' },
    blueBtn: { backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
    redNavigateBtn: { backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' },
    locationBadge: {
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      backgroundColor: locationStatus === 'granted' ? '#064e3b' : '#1e3a5f',
      color: locationStatus === 'granted' ? '#34d399' : '#93c5fd',
      padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold',
    },
    floatingFollowBtn: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      zIndex: 1000,
      backgroundColor: '#eab308',
      color: 'black',
      border: 'none',
      borderRadius: '40px',
      padding: '10px 20px',
      fontWeight: 'bold',
      cursor: 'pointer',
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      transition: 'transform 0.1s ease, background-color 0.2s',
    },
    dropdown: {
      position: 'relative',
      display: 'inline-block',
    },
    dropdownContent: {
      display: 'block',
      position: 'absolute',
      left: '50%',
      transform: 'translateX(-50%)',
      top: '100%',
      backgroundColor: '#1f2937',
      minWidth: '280px',
      boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
      borderRadius: '12px',
      padding: '8px 0',
      zIndex: 1000,
      marginTop: '8px',
      border: '1px solid #374151',
      overflow: 'hidden',
    },
    dropdownItem: {
      color: 'white',
      padding: '12px 24px',
      textDecoration: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      cursor: 'pointer',
      border: 'none',
      background: 'none',
      width: '100%',
      textAlign: 'center',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      fontFamily: 'sans-serif',
    },
    dropdownDivider: {
      borderTop: '1px solid #374151',
      margin: '4px 16px',
    },
    vehicleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 'bold',
    },
  };

  if (loading) return <div style={{ color: 'white', padding: '20px' }}>Loading driver dashboard...</div>;

  return (
    <div style={s.page}>
      <EmergencyLocator
        driverId={driverId}
        onEmergencyTriggered={(data) => {
          setEmergencyTriggered(true);
          console.log('🚨 Emergency triggered:', data);
        }}
      />

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h1 style={{ color: '#eab308', fontSize: '28px', margin: 0 }}>VTA</h1>
          <p style={{ color: '#9ca3af', margin: '4px 0 0' }}>Welcome back, {driverName}!</p>
        </div>

        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '30px',
            flexWrap: 'wrap',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#60a5fa' }}>
                R{earnings.toFixed(2)}
              </div>
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>Total Earnings</div>
              <div style={{ fontSize: '12px', color: '#eab308' }}>
                {jobsCompleted} jobs completed
              </div>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingLeft: '30px',
              borderLeft: '1px solid #374151',
            }}>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#34d399' }}>
                R{walletBalance.toFixed(2)}
              </div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                💰 Available for Withdrawal
              </div>
            </div>

            {emergencyTriggered && (
              <div style={{
                backgroundColor: '#ef4444',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 'bold',
                animation: 'emergencyPulse 1s infinite'
              }}>
                🚨 EMERGENCY MODE
              </div>
            )}
          </div>

          <div className="dropdown-container" style={s.dropdown}>
            <button
              onClick={toggleDropdown}
              style={{
                ...s.yellowBtn,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 30px',
                minWidth: '140px',
                justifyContent: 'center',
                margin: '0 auto',
              }}
            >
              <span>☰</span> Menu
            </button>

            {showDropdown && (
              <div style={s.dropdownContent}>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setShowProfileModal(true);
                  }}
                  style={s.dropdownItem}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: '18px' }}>👤</span>
                  <span>My Profile</span>
                </button>

                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setShowBankingModal(true);
                  }}
                  style={s.dropdownItem}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#374151'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span style={{ fontSize: '18px' }}>💳</span>
                  <span>Banking Details</span>
                </button>

                <div style={s.dropdownDivider} />

                <button
                  onClick={() => {
                    setShowDropdown(false);
                    contactAdmin();
                  }}
                  style={{
                    ...s.dropdownItem,
                    color: '#60a5fa',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#1a2540';
                    e.currentTarget.style.color = '#93c5fd';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#60a5fa';
                  }}
                >
                  <span style={{ fontSize: '18px' }}>📧</span>
                  <span>Contact Admin</span>
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '10px',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    Support
                  </span>
                </button>

                <div style={s.dropdownDivider} />

                <div style={{
                  ...s.dropdownItem,
                  cursor: 'default',
                  fontSize: '12px',
                  color: '#9ca3af',
                }}>
                  <span style={{ fontSize: '16px' }}>
                    {locationStatus === 'granted' ? '📍' : '📍'}
                  </span>
                  <span>
                    {locationStatus === 'granted' ? 'GPS Active' : 'GPS Inactive'}
                  </span>
                  <span style={{
                    marginLeft: 'auto',
                    fontSize: '10px',
                    color: locationStatus === 'granted' ? '#34d399' : '#ef4444'
                  }}>
                    {locationStatus === 'granted' ? '✅' : '❌'}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ width: '120px' }}></div>
      </div>

      <LocationPermissionBanner status={locationStatus} onRequest={requestLocation} />

      {activeJob && (
        <>
          <div style={s.activeCard}>
            <h2 style={{ color: '#eab308', marginBottom: '15px' }}>🚚 ACTIVE JOB</h2>
            <div style={s.grid2}>
              <div>
                <div style={s.label}>🟢 PICKUP</div>
                <div style={s.value}>{activeJob.pickup_address}</div>
              </div>
              <div>
                <div style={s.label}>📍 DROP-OFF</div>
                <div style={s.value}>{activeJob.dropoff_address}</div>
              </div>
              <div>
                <div style={s.label}>💰 AMOUNT</div>
                <div style={{ fontWeight: 'bold', color: '#eab308' }}>R{(activeJob.price_cents / 100).toFixed(2)}</div>
              </div>
              <div>
                <div style={s.label}>STATUS</div>
                <span style={{
                  backgroundColor: activeJob.status === 'assigned' ? '#eab308' : '#10b981',
                  color: 'black', padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold',
                }}>
                  {activeJob.status.toUpperCase()}
                </span>
              </div>
            </div>

            {activeJob.inventory && Object.keys(activeJob.inventory).some(key => activeJob.inventory[key] > 0) && (
              <div style={{ marginTop: '16px' }}>
                {renderInventory(activeJob.inventory)}
              </div>
            )}

            <TurnByTurnPanel
              routeInfo={routeInfo}
              destinationType={activeJob.status === 'picked_up' ? 'dropoff' : 'pickup'}
            />

            {activeJob.customer_name && (
              <div style={{ marginTop: '16px', padding: '14px', backgroundColor: '#111827', borderRadius: '8px', borderTop: '1px solid #eab308' }}>
                <div style={{ fontSize: '14px', color: '#eab308', marginBottom: '10px', fontWeight: 'bold' }}>👤 Customer</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <div><strong>Name:</strong> {activeJob.customer_name}</div>
                  {activeJob.customer_phone && (
                    <a href={`tel:${activeJob.customer_phone}`} style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 'bold' }}>
                      📞 Call Customer
                    </a>
                  )}
                </div>
              </div>
            )}

            {activeJob.special_instructions && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#111827', borderRadius: '8px', borderLeft: '4px solid #3b82f6' }}>
                <div style={{ fontSize: '13px', color: '#3b82f6', fontWeight: 'bold', marginBottom: '5px' }}>📝 Special Instructions</div>
                <div style={{ fontSize: '13px', color: '#d1d5db' }}>{activeJob.special_instructions}</div>
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {activeJob.status === 'assigned' && (
                <button style={s.yellowBtn} onClick={() => setShowPinModal(true)}>
                  📦 CONFIRM PICKUP (Enter PIN)
                </button>
              )}
              {activeJob.status === 'picked_up' && (
                <button style={s.greenBtn} onClick={() => updateStatus(activeJob.id, 'delivered')}>
                  ✅ MARK AS DELIVERED
                </button>
              )}
              {(activeJob.status === 'assigned' || activeJob.status === 'picked_up') && (
                <button
                  style={s.redBtn}
                  onClick={handleCancelClick}
                >
                  🚫 CANCEL TRIP
                </button>
              )}
            </div>
          </div>

          {/* Map Section */}
          <div
            ref={mapContainerRef}
            style={{
              backgroundColor: '#1f2937',
              borderRadius: isFullscreen ? '0' : '12px',
              padding: isFullscreen ? '0' : '20px',
              marginBottom: '20px',
              overflow: 'hidden',
              position: isFullscreen ? 'fixed' : 'relative',
              top: isFullscreen ? 0 : 'auto',
              left: isFullscreen ? 0 : 'auto',
              height: isFullscreen ? '100vh' : 'auto',
              width: isFullscreen ? '100vw' : 'auto',
              zIndex: isFullscreen ? 9999 : 1,
            }}
          >
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '12px',
              padding: isFullscreen ? '10px 20px' : '0',
              backgroundColor: isFullscreen ? '#1f2937' : 'transparent',
            }}>
              <h2 style={{ color: '#eab308', margin: 0 }}>
                {activeJob.status === 'picked_up' ? '🗺️ Navigate to Drop-off' : '🗺️ Navigate to Pickup'}
              </h2>
              <button
                onClick={toggleFullscreen}
                style={{ backgroundColor: '#374151', color: '#eab308', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}
              >
                {isFullscreen ? '✕ EXIT' : '⛶ FULLSCREEN'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap', padding: isFullscreen ? '0 20px' : '0' }}>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>🟢 Pickup</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>📍 Drop-off</span>
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>🚚 You</span>
              <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 'bold' }}>━ To pickup</span>
              <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 'bold' }}>━ To drop-off</span>
            </div>

            {/* 🗺️ GOOGLE MAPS TURN-BY-TURN NAVIGATION BUTTONS */}
            <div style={{
              display: 'flex',
              gap: '10px',
              marginBottom: '12px',
              padding: isFullscreen ? '0 20px' : '0',
            }}>
              {activeJob.status === 'assigned' && activeJob.pickup_lat && activeJob.pickup_lng && (
                <button
                  onClick={() => openInGoogleMaps(activeJob.pickup_lat, activeJob.pickup_lng)}
                  style={{
                    flex: 1,
                    ...s.blueBtn,
                    padding: '14px 20px',
                    fontSize: '15px',
                    boxShadow: '0 2px 6px rgba(59,130,246,0.4)',
                  }}
                >
                  🗺️ OPEN GOOGLE MAPS — TO PICKUP                </button>
              )}

              {activeJob.status === 'picked_up' && activeJob.dropoff_lat && activeJob.dropoff_lng && (
                <button
                  onClick={() => openInGoogleMaps(activeJob.dropoff_lat, activeJob.dropoff_lng)}
                  style={{
                    flex: 1,
                    ...s.redNavigateBtn,
                    padding: '14px 20px',
                    fontSize: '15px',
                    boxShadow: '0 2px 6px rgba(239,68,68,0.4)',
                  }}
                >
                  🗺️ OPEN GOOGLE MAPS — TO DROPOFF

                </button>
              )}
            </div>

            <div style={{ height: isFullscreen ? 'calc(100vh - 180px)' : '480px', position: 'relative', zIndex: 1, touchAction: 'pan-x pan-y' }}>
              <TrackingMap
                pickupLat={activeJob.pickup_lat}
                pickupLng={activeJob.pickup_lng}
                dropoffLat={activeJob.dropoff_lat}
                dropoffLng={activeJob.dropoff_lng}
                driverLat={driverDisplayLat}
                driverLng={driverDisplayLng}
                driverName={driverName}
                status={activeJob.status}
                isLiveGPS={locationStatus === 'granted'}
                onRouteUpdate={setRouteInfo}
                followDriver={followDriver}
                onMapReady={(map) => { driverMapRef.current = map; }}
              />
              <button
                onClick={() => setFollowDriver(!followDriver)}
                style={s.floatingFollowBtn}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fbbf24'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#eab308'}
              >
                {followDriver ? '📍 Unfollow' : '🎯 Follow Me'}
              </button>
            </div>

            {!isFullscreen && (
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                {locationStatus === 'granted'
                  ? '📍 Sharing your live GPS with the customer. Click the button above for Google Maps turn-by-turn navigation, or expand the panel for in-app directions.'
                  : '⚠️ Enable GPS above for live tracking and turn-by-turn navigation.'}
              </div>
            )}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <Chat jobId={activeJob.id} currentUserId={driverId} currentUserName={driverName} userRole="driver" />
          </div>
        </>
      )}

      <div style={s.card}>
        <h2 style={{ color: '#eab308', marginBottom: '15px' }}>
          📋 AVAILABLE JOBS
          {availableJobs.length > 0 && (
            <span style={{
              fontSize: '14px',
              color: '#9ca3af',
              fontWeight: 'normal',
              marginLeft: '10px'
            }}>
              ({availableJobs.length} nearby)
            </span>
          )}
        </h2>
        {!availableJobs.length ? (
          <p style={{ color: '#9ca3af', textAlign: 'center' }}>
            {activeJob ? '🔄 No other jobs available nearby right now.' : 'No available jobs at the moment.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {availableJobs.map((job) => {
              const vehicleData = getVehicleGif(job.vehicle_type);
              const distance = job.distance;
              const estimatedTime = job.estimatedTime;

              return (
                <div key={job.id} style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '15px', border: '1px solid #374151' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ flex: 2 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                        🟢 {job.pickup_address} → 📍 {job.dropoff_address}
                      </div>

                      <div style={{ display: 'flex', gap: '20px', fontSize: '12px', color: '#9ca3af', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span>💰 R{(job.price_cents / 100).toFixed(2)}</span>

                        <div style={{
                          ...s.vehicleBadge,
                          backgroundColor: `${vehicleData.color}20`,
                          border: `1px solid ${vehicleData.color}`,
                          color: vehicleData.color,
                          padding: '2px 12px',
                        }}>
                          <img
                            src={vehicleData.gif}
                            alt={vehicleData.label}
                            style={{
                              width: '24px',
                              height: '24px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                            }}
                          />
                          <span>{vehicleData.label}</span>
                        </div>

                        {job.has_trailer && <span>🔗 Trailer Required</span>}
                      </div>

                      <div style={{
                        marginTop: '6px',
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap',
                        fontSize: '12px',
                        alignItems: 'center'
                      }}>
                        {distance !== null && distance !== undefined ? (
                          <>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#60a5fa',
                              fontWeight: 'bold'
                            }}>
                              📏 <span style={{ color: '#d1d5db', fontWeight: 'normal' }}>Distance:</span> {formatDistance(distance)}
                            </span>
                            {estimatedTime !== null && estimatedTime !== undefined && (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#34d399',
                                fontWeight: 'bold'
                              }}>
                                ⏱️ <span style={{ color: '#d1d5db', fontWeight: 'normal' }}>Est. Time:</span> ~{estimatedTime} min
                              </span>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#6b7280', fontSize: '11px' }}>
                            ⚠️ Enable GPS to see distance and estimated time
                          </span>
                        )}
                      </div>

                      <div style={{ marginTop: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {job.vehicle_type?.toLowerCase().includes('bike') && (
                          <div style={{
                            ...s.vehicleBadge,
                            backgroundColor: '#1e3a5f',
                            border: '1px solid #60a5fa',
                            color: '#60a5fa',
                          }}>
                            <img
                              src={vehicleGifs.bike.gif}
                              alt="Bike"
                              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                            />
                            <span>BIKE</span>
                          </div>
                        )}

                        {job.vehicle_type?.toLowerCase().includes('car') && !job.vehicle_type?.toLowerCase().includes('passenger') && (
                          <div style={{
                            ...s.vehicleBadge,
                            backgroundColor: '#4a1a6b',
                            border: '1px solid #c084fc',
                            color: '#c084fc',
                          }}>
                            <img
                              src={vehicleGifs.car.gif}
                              alt="Private Car"
                              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                            />
                            <span>PRIVATE CAR</span>
                          </div>
                        )}

                        {job.vehicle_type?.toLowerCase().includes('passenger') && (
                          <div style={{
                            ...s.vehicleBadge,
                            backgroundColor: '#0d4a5f',
                            border: '1px solid #67e8f9',
                            color: '#67e8f9',
                          }}>
                            <img
                              src={vehicleGifs['passenger+cargo'].gif}
                              alt="Passenger + Cargo"
                              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                            />
                            <span>PASSENGER + CARGO</span>
                          </div>
                        )}

                        {job.vehicle_type?.toLowerCase() === 'van' && !job.vehicle_type?.toLowerCase().includes('trailer') && (
                          <div style={{
                            ...s.vehicleBadge,
                            backgroundColor: '#064e3b',
                            border: '1px solid #34d399',
                            color: '#34d399',
                          }}>
                            <img
                              src={vehicleGifs.van.gif}
                              alt="Van"
                              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                            />
                            <span>VAN</span>
                          </div>
                        )}

                        {job.vehicle_type?.toLowerCase().includes('trailer') && (
                          <div style={{
                            ...s.vehicleBadge,
                            backgroundColor: '#3b0000',
                            border: '1px solid #ef4444',
                            color: '#ef4444',
                          }}>
                            <img
                              src={vehicleGifs['van+trailer'].gif}
                              alt="Van + Trailer"
                              style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                            />
                            <span>VAN + TRAILER</span>
                          </div>
                        )}

                        {job.has_trailer && (
                          <span style={{ backgroundColor: '#3b0000', color: '#ef4444', padding: '2px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold' }}>
                            🔗 TRAILER
                          </span>
                        )}
                      </div>

                      {job.inventory && Object.keys(job.inventory).some(key => job.inventory[key] > 0) && (
                        renderInventory(job.inventory)
                      )}

                      {job.special_instructions && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px 12px',
                          backgroundColor: '#1a1a2e',
                          borderRadius: '6px',
                          borderLeft: '3px solid #3b82f6',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: '8px'
                        }}>
                          <span style={{ fontSize: '14px' }}>📝</span>
                          <div>
                            <div style={{
                              fontSize: '11px',
                              color: '#3b82f6',
                              fontWeight: 'bold',
                              marginBottom: '2px'
                            }}>
                              Customer Instructions
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#d1d5db',
                              lineHeight: '1.4'
                            }}>
                              {job.special_instructions}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      style={{
                        ...s.yellowBtn,
                        opacity: activeJob ? 0.5 : 1,
                        cursor: activeJob ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => {
                        if (!activeJob) {
                          acceptJob(job.id);
                        } else {
                          alert('⚠️ You already have an active job. Complete or cancel it first before accepting another.');
                        }
                      }}
                      disabled={!!activeJob}
                    >
                      {activeJob ? 'BUSY' : 'ACCEPT JOB'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showConfirmDialog && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '100%', border: '2px solid #ef4444', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
            <h2 style={{ color: '#ef4444', marginBottom: '10px' }}>Cancel Trip?</h2>
            <p style={{ color: '#d1d5db', marginBottom: '20px' }}>
              Are you sure you want to cancel this trip?<br/>
              <span style={{ color: '#9ca3af', fontSize: '14px' }}>
                This action cannot be undone. The job will be returned to the available pool.
              </span>
            </p>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setShowConfirmDialog(false)}
                style={{ flex: 1, padding: '12px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                GO BACK
              </button>
              <button
                onClick={confirmCancel}
                style={{ flex: 1, padding: '12px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
              >
                YES, CANCEL TRIP
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '20px' }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '100%', border: '2px solid #eab308' }}>
            <h2 style={{ color: '#eab308', marginBottom: '10px' }}>🚫 Cancel Trip</h2>
            <p style={{ color: '#9ca3af', marginBottom: '20px', fontSize: '14px' }}>
              Please select a reason for cancelling this trip.
            </p>

            <div style={{ marginBottom: '15px' }}>
              <label style={{ color: '#d1d5db', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                Reason for Cancellation *
              </label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', fontSize: '14px' }}
              >
                <option value="">Select a reason...</option>
                {cancelReasons.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ color: '#d1d5db', fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                Additional Details (Optional)
              </label>
              <textarea
                placeholder="Please provide more details about why you're cancelling this trip..."
                value={cancelSubReason}
                onChange={(e) => setCancelSubReason(e.target.value)}
                style={{ width: '100%', padding: '12px', borderRadius: '6px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', minHeight: '80px', boxSizing: 'border-box', fontSize: '14px' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowCancelModal(false); setCancelReason(''); setCancelSubReason(''); }}
                style={{ flex: 1, padding: '12px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                CANCEL
              </button>
              <button
                onClick={cancelTrip}
                disabled={!cancelReason || isCancelling}
                style={{
                  flex: 2,
                  padding: '12px',
                  backgroundColor: (!cancelReason || isCancelling) ? '#4b5563' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: (!cancelReason || isCancelling) ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold',
                  opacity: (!cancelReason || isCancelling) ? 0.6 : 1
                }}
              >
                {isCancelling ? '⏳ CANCELLING...' : '🚫 CANCEL TRIP'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProfileModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '100%', border: '2px solid #eab308', maxHeight: '90vh', overflowY: 'auto' }}>
            {profileLoading ? (
              <div style={{ textAlign: 'center', color: '#eab308' }}>Loading profile...</div>
            ) : driverProfile ? (
              <>
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={driverProfile.avatar}
                    alt={driverProfile.full_name}
                    style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #eab308', marginBottom: '10px' }}
                  />
                  <h2 style={{ color: '#eab308', margin: '0 0 5px 0' }}>{driverProfile.full_name}</h2>
                  <p style={{ color: '#9ca3af', marginBottom: '15px' }}>
                    {driverProfile.is_online ? '🟢 Online' : '⚫ Offline'}
                  </p>
                </div>
                <div style={{ textAlign: 'left' }}>
                  <p><strong>📧 Email:</strong> {driverProfile.email}</p>
                  <p><strong>📞 Phone:</strong> {driverProfile.phone || 'Not provided'}</p>
                  <p><strong>🚗 Vehicle:</strong> {driverProfile.vehicle.type} {driverProfile.vehicle.color ? `(${driverProfile.vehicle.color})` : ''}</p>
                  <p><strong>🔢 Plate:</strong> {driverProfile.vehicle.plate_number || 'Not provided'}</p>
                  <p><strong>📅 Joined:</strong> {new Date(driverProfile.joined_date).toLocaleDateString()}</p>
                  <hr style={{ borderColor: '#374151' }} />
                  <p><strong>⭐ Rating:</strong> {driverProfile.stats.avg_rating} / 5 ({driverProfile.stats.review_count} reviews)</p>
                  <p><strong>✅ Jobs Completed:</strong> {driverProfile.stats.jobs_completed}</p>
                  <p><strong>💰 Total Earnings:</strong> R{driverProfile.stats.total_earned_zar.toFixed(2)}</p>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: '#ef4444' }}>Failed to load profile. Please try again later.</div>
            )}
            <button onClick={() => setShowProfileModal(false)} style={{ ...s.yellowBtn, width: '100%', marginTop: '15px' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {showBankingModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', width: '400px', border: '2px solid #eab308' }}>
            <h2 style={{ color: '#eab308', marginBottom: '20px' }}>🏦 Banking Details</h2>
            <input
              type="text"
              placeholder="e.g., Bank Name"
              value={bankingDetails.bank_name}
              onChange={(e) => setBankingDetails({...bankingDetails, bank_name: e.target.value})}
              style={{ ...s.input, marginBottom: '10px' }}
            />
            <input
              type="text"
              placeholder="e.g., Branch Code"
              value={bankingDetails.branch_code}
              onChange={(e) => setBankingDetails({...bankingDetails, branch_code: e.target.value})}
              style={{ ...s.input, marginBottom: '10px' }}
            />
            <input
              type="text"
              placeholder="e.g., 123456789"
              value={bankingDetails.account_number}
              onChange={(e) => setBankingDetails({...bankingDetails, account_number: e.target.value})}
              style={{ ...s.input, marginBottom: '10px' }}
            />
            <input
              type="text"
              placeholder="e.g., Savings/Chaque"
              value={bankingDetails.account_name}
              onChange={(e) => setBankingDetails({...bankingDetails, account_name: e.target.value})}
              style={{ ...s.input, marginBottom: '20px' }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowBankingModal(false)} style={{ flex: 1, padding: '10px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px' }}>
                Cancel
              </button>
              <button onClick={saveBankingDetails} disabled={loadingBanking} style={{ flex: 1, padding: '10px', backgroundColor: '#eab308', color: 'black', border: 'none', borderRadius: '6px', fontWeight: 'bold' }}>
                {loadingBanking ? 'Saving...' : 'Save'}
              </button>
            </div>
            {savedBanking && savedBanking.bank_verified && (
              <p style={{ marginTop: '15px', fontSize: '12px', color: '#10b981' }}>✅ Verified – ready for payouts</p>
            )}
            {savedBanking && !savedBanking.bank_verified && (
              <p style={{ marginTop: '15px', fontSize: '12px', color: '#eab308' }}>⏳ Awaiting verification</p>
            )}
          </div>
        </div>
      )}

      {showPinModal && activeJob && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', padding: '30px', width: '350px', border: '2px solid #eab308', textAlign: 'center' }}>
            <h2 style={{ color: '#eab308', marginBottom: '10px' }}>🔐 Enter Safety PIN</h2>
            <p style={{ color: '#9ca3af', marginBottom: '20px', fontSize: '14px' }}>
              Ask the customer for their 4-digit PIN to confirm pickup.
            </p>
            <input
              type="text"
              maxLength="4"
              placeholder="0000"
              value={pinInput}
              onChange={(e) => setPinInput(e.target.value.replace(/[^0-9]/g, ''))}
              style={{ width: '100%', padding: '15px', fontSize: '28px', textAlign: 'center', borderRadius: '8px', border: '1px solid #374151', backgroundColor: '#111827', color: 'white', marginBottom: '20px', letterSpacing: '12px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowPinModal(false); setPinInput(''); }}
                style={{ flex: 1, padding: '12px', backgroundColor: '#374151', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
              >
                CANCEL
              </button>
              <button
                onClick={() => updateStatus(activeJob.id, 'picked_up', pinInput)}
                disabled={pinInput.length !== 4}
                style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '6px', fontWeight: 'bold', backgroundColor: pinInput.length === 4 ? '#eab308' : '#4b5563', color: pinInput.length === 4 ? 'black' : '#9ca3af', cursor: pinInput.length === 4 ? 'pointer' : 'not-allowed' }}
              >
                VERIFY & CONFIRM PICKUP
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes emergencyPulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </div>
  );
};

export default DriverDashboard;
